import type React from "react"
import type { ReactNode } from "react"

interface BroadcastFrameProps {
  children: ReactNode
}

/**
 * Full-bleed dark broadcast wrapper with radial gradient, vignette, and CRT scanlines.
 * Breaks out of the parent max-w container to fill the viewport width.
 */
export function BroadcastFrame({ children }: BroadcastFrameProps) {
  return (
    <div
      className="relative"
      style={
        {
          width: "100vw",
          marginLeft: "calc(-50vw + 50%)",
          "--neon-green": "oklch(0.8 0.3 145)",
          "--neon-green-dim": "oklch(0.6 0.22 145)",
          "--neon-gold": "oklch(0.85 0.28 85)",
          "--neon-gold-dim": "oklch(0.65 0.2 85)",
          minHeight: "100vh",
          background: "oklch(0.09 0.01 55)",
          color: "var(--foreground)",
        } as React.CSSProperties
      }
    >
      {/* TV-banded texture (matches body treatment) */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, oklch(1 0 0 / 0.08) 0px, oklch(1 0 0 / 0.08) 1px, transparent 1px, transparent 4px)",
          zIndex: 0,
        }}
      />

      {/* Cinematic vignette */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          boxShadow:
            "inset 0 0 200px 60px oklch(0 0 0 / 0.5), inset 0 0 80px 20px oklch(0 0 0 / 0.3)",
          zIndex: 1,
        }}
      />

      {/* CRT scanlines */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 3px, oklch(0 0 0 / 0.03) 3px, oklch(0 0 0 / 0.03) 6px)",
          zIndex: 2,
        }}
      />

      {/* Content */}
      <div className="relative" style={{ zIndex: 3 }}>
        {children}
      </div>
    </div>
  )
}
